<div class="w-full max-w-6xl mx-auto p-4 sm:p-6">
  <!-- Header with Close Button -->
  <div class="flex justify-end items-center mb-6 sm:mb-8">
    <button (click)="dialogRef.close()" class="text-gray-500 hover:text-gray-700 transition-colors duration-200 ml-4">
      <svg class="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    </button>
  </div>
  <div class="flex justify-center items-center mb-6 sm:mb-8">
    <div class="text-center flex-1">
      <h1 class="text-2xl sm:text-3xl font-bold text-primary mb-2">Internet Speed Test</h1>
      <p class="text-sm sm:text-base text-gray-600">Test your connection speed with precision</p>
    </div>
  </div>

  <!-- Speed Test Container -->
  <div class="text-center">
    <!-- Start Button -->
    <div *ngIf="!isRunning && !results" class="mb-8">
      <button 
        (click)="startSpeedTest()" 
        class="bg-accent hover:bg-yellow-600 text-black font-bold py-4 px-8 rounded-lg text-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105">
        Start Speed Test
      </button>
    </div>

    <!-- Testing State -->
    <div *ngIf="isRunning" class="mb-6 sm:mb-8">
      <div class="bg-white rounded-lg p-6 sm:p-8">
        <!-- Animated Loading -->
        <div class="flex flex-col items-center space-y-4 sm:space-y-6">
          <!-- Glowing Circle -->
          <div class="inner"></div>
          
          <!-- Status Text -->
          <div class="text-center">
            <h3 class="text-xl sm:text-2xl font-bold text-primary mb-2">Testing Your Connection</h3>
            <p class="text-base sm:text-lg text-gray-600 mb-2">Measuring download, upload, and ping speeds</p>
          </div>

          <!-- Bouncing Dots -->
          <div class="spinner">
            <div class="bounce1"></div>
            <div class="bounce2"></div>
            <div></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Results Display -->
    <div *ngIf="results && !isRunning" class="space-y-6">
      <!-- Success Header -->
      <div class="text-center mb-6">
        <div class="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4 shadow-lg">
          <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h3 class="text-2xl font-semibold text-primary mb-2">Speed Test Complete!</h3>
        <p class="text-gray-600">Your internet speed has been measured successfully</p>
      </div>

      <!-- Results Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <!-- Download Speed -->
        <div class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div class="flex items-center justify-center mb-3 sm:mb-4">
            <div class="w-12 h-12 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center shadow-md">
              <svg class="w-6 h-6 sm:w-8 sm:h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"></path>
              </svg>
            </div>
          </div>
          <h3 class="text-lg sm:text-xl font-semibold text-gray-800 text-center mb-2 sm:mb-3">Download Speed</h3>
          <div class="text-3xl sm:text-5xl font-bold text-center mb-2 text-green-600">
            {{ results.downloadSpeed | number:'1.2-2' }}
          </div>
          <p class="text-base sm:text-lg text-center text-gray-600 font-medium mb-1">Mbps</p>
          <p class="text-xs sm:text-sm text-center text-gray-500">Data received from server</p>
        </div>

        <!-- Upload Speed -->
        <div class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
          <div class="flex items-center justify-center mb-3 sm:mb-4">
            <div class="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center shadow-md">
              <svg class="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
          </div>
          <h3 class="text-lg sm:text-xl font-semibold text-gray-800 text-center mb-2 sm:mb-3">Upload Speed</h3>
          <div class="text-3xl sm:text-5xl font-bold text-center mb-2 text-blue-600">
            {{ results.uploadSpeed | number:'1.2-2' }}
          </div>
          <p class="text-base sm:text-lg text-center text-gray-600 font-medium mb-1">Mbps</p>
          <p class="text-xs sm:text-sm text-center text-gray-500">Data sent to server</p>
        </div>

        <!-- Ping -->
        <div class="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 sm:col-span-2 lg:col-span-1">
          <div class="flex items-center justify-center mb-3 sm:mb-4">
            <div class="w-12 h-12 sm:w-16 sm:h-16 bg-accent rounded-full flex items-center justify-center shadow-md">
              <svg class="w-6 h-6 sm:w-8 sm:h-8 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
          </div>
          <h3 class="text-lg sm:text-xl font-semibold text-gray-800 text-center mb-2 sm:mb-3">Ping</h3>
          <div class="text-3xl sm:text-5xl font-bold text-center mb-2 text-primary">
            {{ results.ping | number:'1.0-0' }}
          </div>
          <p class="text-base sm:text-lg text-center text-gray-600 font-medium mb-1">ms</p>
          <p class="text-xs sm:text-sm text-center text-gray-500">Response time to server</p>
        </div>
      </div>

      <!-- Test Again Button -->
      <div class="text-center pt-6">
        <button (click)="startSpeedTest()"
          class="bg-accent hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg text-md transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105">
          Test Again
        </button>
      </div>
    </div>

    <!-- Error State -->
    <div *ngIf="error" class="bg-red-50 border border-red-200 rounded-lg p-6 shadow-lg">
      <div class="flex items-center justify-center mb-2">
        <svg class="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <h3 class="text-xl font-semibold text-red-800">Test Failed</h3>
      </div>
      <p class="text-red-700 mb-4 text-center">{{ error }}</p>
      <div class="text-center">
        <button 
          (click)="startSpeedTest()" 
          class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl">
          Try Again
        </button>
      </div>
    </div>
  </div>
</div>
